import React from "react";

/**
 * <SmartImage /> — responsive image for locally-cached article heroes.
 *
 * Given a base src of `/api/static/articles/<slug>.jpg`, it renders a
 * <picture> element with:
 *   - <source type="image/avif"> pointing at multi-width AVIF variants
 *     `/api/static/articles/<slug>-800.avif 800w, ...-1200.avif 1200w, ...-1600.avif 1600w`
 *   - <source type="image/webp"> for WebP (broader browser support)
 *   - <img> fallback pointing at the base .jpg
 *
 * Browsers pick the first <source> whose format they support (AVIF → WebP → JPEG).
 *
 * Falls back cleanly for any src that doesn't match the /api/static/articles/
 * pattern (e.g. `/assets/*.png` bundled with the frontend, or external URLs) —
 * in that case we just render a plain <img>.
 *
 * Also always sets `loading="lazy"` and `decoding="async"` for CLS + perf,
 * unless the caller opts into eager loading (e.g. the above-the-fold hero).
 */
export default function SmartImage({
  src,
  alt = "",
  sizes = "(min-width: 1024px) 50vw, 100vw",
  className,
  eager = false,
  ...rest
}) {
  // Safety net: if the browser reports the <img> broken (e.g. stale poisoned
  // cache, transient network), replace src with a fresh cache-bust query so
  // the next request bypasses any bad cached response and re-hits the origin.
  // Fires at most once per element to avoid loops.
  const handleError = React.useCallback((e) => {
    const el = e.currentTarget;
    if (el.dataset.retried === "1") return;
    el.dataset.retried = "1";
    try {
      const u = new URL(el.src, window.location.origin);
      u.searchParams.set("_r", Date.now().toString(36));
      el.src = u.pathname + u.search;
    } catch {
      // best-effort
    }
  }, []);

  const commonImgProps = {
    alt,
    loading: eager ? "eager" : "lazy",
    decoding: "async",
    fetchPriority: eager ? "high" : "auto",
    className,
    onError: handleError,
    ...rest,
  };

  // Match `/api/static/articles/<slug>.jpg` with an optional cache-buster
  // query string (e.g. `?v=2`). We preserve the query string on the variant
  // URLs too, so a cache-bust bump invalidates every WebP/AVIF variant.
  const match = typeof src === "string"
    ? src.match(/^(.*\/api\/static\/articles\/)([^/?]+?)\.jpg(\?[^#]*)?$/i)
    : null;

  if (!match) {
    return <img src={src} {...commonImgProps} />;
  }

  const [, prefix, slug, query = ""] = match;
  const w = (n, ext) => `${prefix}${slug}-${n}.${ext}${query} ${n}w`;
  const avifSrcset = [w(800, "avif"), w(1200, "avif"), w(1600, "avif")].join(", ");
  const webpSrcset = [w(800, "webp"), w(1200, "webp"), w(1600, "webp")].join(", ");

  return (
    <picture>
      {/* AVIF first (best compression, ~50% smaller than JPEG). Browsers that
          don't support it silently skip to the next <source>. */}
      <source type="image/avif" srcSet={avifSrcset} sizes={sizes} />
      <source type="image/webp" srcSet={webpSrcset} sizes={sizes} />
      <img src={src} {...commonImgProps} />
    </picture>
  );
}
